import React, { useState } from 'react';
import type {
  CatalogClientMapping,
  CatalogDirection,
  CatalogRoleAccess,
  CatalogService,
} from '../types';
import { DirectionSettingsDrawer } from './DirectionSettingsDrawer';
import { RolePreviewModal } from './RolePreviewModal';

const ROLES_LIST = [
  { code: 'SYSTEM_OWNER', label: 'Владелец системы', mode: 'FULL' },
  { code: 'OPERATIONS_PARTNER', label: 'Операционный партнёр', mode: 'FULL' },
  { code: 'MENTOR_PARTNER', label: 'Научный партнёр / Ментор', mode: 'SELECTED' },
  { code: 'CLIENT', label: 'Клиент портала', mode: 'CLIENT_9' },
  { code: 'AUTHOR', label: 'Автор / Соавтор', mode: 'LIMITED' },
  { code: 'PROJECT_EDITOR', label: 'Редактор проекта', mode: 'FULL' },
  { code: 'RESEARCHER', label: 'Исследователь', mode: 'SELECTED' },
  { code: 'DEPARTMENT_HEAD', label: 'Руководитель кафедры', mode: 'RESTRICTED' },
  { code: 'DEPARTMENT_ASSISTANT', label: 'Лаборант кафедры', mode: 'RESTRICTED' },
  { code: 'LEARNER', label: 'Докторант', mode: 'RESTRICTED' },
  { code: 'STUDENT', label: 'Обучающийся', mode: 'RESTRICTED' },
  { code: 'ADVISOR', label: 'Эдвайзер', mode: 'RESTRICTED' },
  { code: 'SUPERVISOR', label: 'Научный руководитель', mode: 'RESTRICTED' },
  { code: 'TEACHER', label: 'Преподаватель', mode: 'RESTRICTED' },
  { code: 'PROJECT_MEMBER', label: 'Участник проекта', mode: 'RESTRICTED' },
];

interface CoverageRoleAccessTabProps {
  directions: CatalogDirection[];
  services: CatalogService[];
  roleAccess: CatalogRoleAccess[];
  clientMappings: CatalogClientMapping[];
  onToggleServiceAccess: (roleCode: string, serviceId: string, currentAccess: string) => void;
  onAllowAllDirection: (roleCode: string, directionId: string) => void;
  onDenyAllDirection: (roleCode: string, directionId: string) => void;
  onResetDirectionPolicy: (roleCode: string, directionId: string) => void;
  onUpdateClientMapping: (clientCategoryId: string, targetServiceId: string) => void;
  onNavigateToServiceReview: (serviceId: string) => void;
}

export const CoverageRoleAccessTab: React.FC<CoverageRoleAccessTabProps> = ({
  directions,
  services,
  roleAccess,
  clientMappings,
  onToggleServiceAccess,
  onAllowAllDirection,
  onDenyAllDirection,
  onResetDirectionPolicy,
  onUpdateClientMapping,
  onNavigateToServiceReview,
}) => {
  const [activeRole, setActiveRole] = useState<string>('MENTOR_PARTNER');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDirections, setExpandedDirections] = useState<Record<string, boolean>>({
    DIR_01: true,
    DIR_10: true,
    DIR_11: true,
  });
  const [selectedDirectionForSettings, setSelectedDirectionForSettings] = useState<CatalogDirection | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const toggleDirectionExpand = (dirId: string) => {
    setExpandedDirections((prev) => ({
      ...prev,
      [dirId]: !prev[dirId],
    }));
  };

  const getServiceAccess = (serviceId: string): CatalogRoleAccess | undefined =>
    roleAccess.find((ra) => ra.role_code === activeRole && ra.service_id === serviceId);

  const isRoleRestricted = ['DEPARTMENT_HEAD', 'DEPARTMENT_ASSISTANT', 'LEARNER', 'STUDENT', 'TEACHER', 'PROJECT_MEMBER'].includes(activeRole);

  const filteredServices = services.filter((s) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return s.title.toLowerCase().includes(q) || s.display_code.toLowerCase().includes(q) || s.service_id.toLowerCase().includes(q);
  });

  return (
    <div className="coverage-main-layout">
      <aside className="coverage-role-sidebar">
        <div className="cov-sidebar-heading">Роли и категории системы</div>
        {ROLES_LIST.map((r) => {
          const isSelected = activeRole === r.code;
          return (
            <button
              key={r.code}
              className={`cov-role-item ${isSelected ? 'active' : ''}`}
              onClick={() => setActiveRole(r.code)}
            >
              <span>{r.label}</span>
              <span className={`cov-role-badge ${r.mode === 'FULL' ? 'cov-badge-allowed' : r.mode === 'SELECTED' ? 'cov-badge-partial' : r.mode === 'CLIENT_9' ? 'cov-badge-info' : 'cov-badge-denied'}`}>
                {r.mode}
              </span>
            </button>
          );
        })}
      </aside>

      <div className="coverage-content-panel">
        <div className="cov-filter-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.125rem' }}>
              {ROLES_LIST.find((r) => r.code === activeRole)?.label || activeRole}
            </h2>
            {isRoleRestricted && (
              <span className="cov-role-badge cov-badge-denied">Создание заявок ограничено</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="text"
              className="cov-search-input"
              placeholder="Поиск по услугам направления..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="cov-btn cov-btn-outline"
              onClick={() => setIsPreviewOpen(true)}
            >
              👁 Посмотреть как роль
            </button>
          </div>
        </div>

        {activeRole === 'CLIENT' && (
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', color: '#f1f5f9' }}>Сопоставление категорий клиента с услугами</h3>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>Клиенты видят упрощённые категории, которые соответствуют услугам каталога</p>
              </div>
              <span className="cov-role-badge cov-badge-info">9 категорий клиента</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.75rem' }}>
              {clientMappings.map((cm) => (
                <div key={cm.client_category_id} style={{ background: '#131d31', border: '1px solid #1e293b', borderRadius: '8px', padding: '0.75rem' }}>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#38bdf8', marginBottom: '0.25rem' }}>
                    {cm.order}. {cm.category_title}
                  </div>
                  <div className="cov-form-group">
                    <label className="cov-form-label" style={{ fontSize: '0.6875rem' }}>Целевая услуга 11×72</label>
                    <select
                      className="cov-form-select"
                      style={{ fontSize: '0.75rem', padding: '0.35rem 0.5rem' }}
                      value={cm.service_id}
                      onChange={(e) => onUpdateClientMapping(cm.client_category_id, e.target.value)}
                    >
                      {services.map((s) => (
                        <option key={s.service_id} value={s.service_id}>
                          {s.display_code} {s.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {directions.map((dir) => {
            const dirServices = filteredServices.filter((s) => s.primary_direction_id === dir.direction_id);
            if (searchQuery.trim() && dirServices.length === 0) return null;

            const allDirServices = services.filter((s) => s.primary_direction_id === dir.direction_id);
            const allowedCount = allDirServices.filter((s) => getServiceAccess(s.service_id)?.access === 'ALLOWED').length;
            const dirStatus = allowedCount === allDirServices.length && allDirServices.length > 0 ? 'ALLOWED' : allowedCount > 0 ? 'PARTIAL' : 'DENIED';
            const isExpanded = !!expandedDirections[dir.direction_id];

            return (
              <div key={dir.direction_id} className={`cov-direction-card ${isExpanded ? 'expanded' : ''}`}>
                <div className="cov-direction-head" onClick={() => toggleDirectionExpand(dir.direction_id)}>
                  <div className="cov-direction-title-group">
                    <span className="cov-dir-code">{dir.display_code}</span>
                    <div>
                      <div className="cov-dir-title">{dir.title}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{dir.description}</div>
                    </div>
                  </div>

                  <div className="cov-dir-meta" onClick={(e) => e.stopPropagation()}>
                    <span className={`cov-role-badge ${dirStatus === 'ALLOWED' ? 'cov-badge-allowed' : dirStatus === 'PARTIAL' ? 'cov-badge-partial' : 'cov-badge-denied'}`}>
                      {dirStatus === 'ALLOWED' ? 'РАЗРЕШЕНО' : dirStatus === 'PARTIAL' ? `ЧАСТИЧНО (${allowedCount}/${allDirServices.length})` : 'ЗАПРЕЩЕНО'}
                    </span>

                    <button
                      className="cov-btn cov-btn-outline"
                      style={{ fontSize: '0.75rem', padding: '0.35rem 0.6rem' }}
                      onClick={() => setSelectedDirectionForSettings(dir)}
                    >
                      ⚙ Настройка
                    </button>

                    <button
                      className="cov-btn cov-btn-outline"
                      style={{ padding: '0.25rem 0.5rem' }}
                      onClick={() => toggleDirectionExpand(dir.direction_id)}
                    >
                      {isExpanded ? '▲' : '▼'}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="cov-service-list">
                    {dirServices.map((srv) => {
                      const accessEntry = getServiceAccess(srv.service_id);
                      const isAllowed = accessEntry?.access === 'ALLOWED';

                      return (
                        <div key={srv.service_id} className="cov-service-row">
                          <div className="cov-service-info">
                            <label className="cov-toggle-switch" title="Включить / отключить доступ к услуге">
                              <input
                                type="checkbox"
                                checked={isAllowed}
                                onChange={() => onToggleServiceAccess(activeRole, srv.service_id, accessEntry?.access || 'DENIED')}
                              />
                              <span className="cov-toggle-slider" />
                            </label>

                            <span className="cov-srv-code">{srv.display_code}</span>

                            <div>
                              <div className="cov-srv-title">{srv.title}</div>
                              <div style={{ fontSize: '0.6875rem', color: '#64748b' }}>
                                Код: {srv.display_code} · Маршрут: {srv.route}
                              </div>
                            </div>
                          </div>

                          <div className="cov-service-actions">
                            <span className={`cov-role-badge ${accessEntry?.source === 'OWNER_OVERRIDE' ? 'cov-badge-partial' : 'cov-badge-info'}`}>
                              {accessEntry?.source === 'OWNER_OVERRIDE'
                                ? 'Переопределение'
                                : accessEntry?.source === 'ROLE_POLICY'
                                  ? 'Политика роли'
                                  : accessEntry?.source || 'Политика роли'}
                            </span>

                            <button
                              className="cov-btn cov-btn-outline"
                              style={{ fontSize: '0.75rem' }}
                              onClick={() => onNavigateToServiceReview(srv.service_id)}
                              title="Перейти к вопросам и предварительным ответам"
                            >
                              Вопросы / предварительные →
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <DirectionSettingsDrawer
        direction={selectedDirectionForSettings}
        roleCode={activeRole}
        services={services}
        isOpen={!!selectedDirectionForSettings}
        onClose={() => setSelectedDirectionForSettings(null)}
        onAllowAll={(dirId) => onAllowAllDirection(activeRole, dirId)}
        onDenyAll={(dirId) => onDenyAllDirection(activeRole, dirId)}
        onResetPolicy={(dirId) => onResetDirectionPolicy(activeRole, dirId)}
      />

      <RolePreviewModal
        roleCode={activeRole}
        directions={directions}
        services={services}
        roleAccess={roleAccess}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </div>
  );
};
